import { useEffect, useMemo, useState } from 'react';
import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, ExcelIcon, exportToExcel,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import { C360Table, ColumnChooser } from './c360';
import { usePageMeta } from '../hooks/usePageMeta';

const API_BASE = '/api/dashboard/salesrep';

const statusTone = (status) => {
  switch (String(status || '').toLowerCase()) {
    case 'open': return 'wait';
    case 'pending': return 'wait';
    case 'completed':
    case 'closed': return 'ok';
    default: return 'neutral';
  }
};

function priorityLabel(priority) {
  switch (priority) {
    case 1: return 'Yüksek';
    case 2: return 'Orta';
    case 3: return 'Düşük';
    default: return priority ?? '-';
  }
}

function formatDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('tr-TR');
}

export function ActionListPage() {
  usePageMeta('Aksiyonlarım', 'Atanan aksiyonları filtreleyin, sıralayın ve takip edin.');

  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [filterInput, setFilterInput] = useState('');
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState([{ id: 'createdDate', desc: true }]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [columnVisibility, setColumnVisibility] = useState({});

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(API_BASE, { headers: authHeaders() });
      if (!res.ok) {
        showToast('error', res.status === 403 ? 'Erişim yetkiniz yok.' : 'Veri yüklenemedi.');
        setActions([]);
        return;
      }
      const json = await res.json();
      const data = json.data ?? json;
      const list = Array.isArray(data.openActions) ? data.openActions : [];
      setActions(list.map((a, i) => ({ id: a.id ?? `act-${i}`, ...a })));
    } catch {
      showToast('error', 'Bağlantı hatası.');
      setActions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const globalFilterFn = (row, _columnId, value) => {
    const search = String(value || '').toLocaleLowerCase('tr-TR');
    if (!search) return true;
    const a = row.original;
    return [a.title, a.description, a.status, a.customerName, priorityLabel(a.priority)]
      .some((v) => String(v ?? '').toLocaleLowerCase('tr-TR').includes(search));
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('title', {
      header: 'Başlık',
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('description', {
      header: 'Açıklama',
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('status', {
      header: 'Durum',
      cell: (info) => { const v = info.getValue(); return v ? <StatusBadge tone={statusTone(v)}>{v}</StatusBadge> : '-'; },
    }),
    columnHelper.accessor('priority', {
      header: 'Öncelik',
      cell: (info) => priorityLabel(info.getValue()),
    }),
    columnHelper.accessor('customerName', {
      header: 'Müşteri',
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('createdDate', {
      header: 'Tarih',
      cell: (info) => formatDate(info.getValue()),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  const table = useReactTable({
    data: actions,
    columns,
    state: { sorting, columnVisibility, globalFilter, pagination },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    globalFilterFn,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const filteredCount = table.getFilteredRowModel().rows.length;

  const applyFilter = (text) => {
    setGlobalFilter(text);
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  };

  const handleExport = () => {
    const exportRows = table.getFilteredRowModel().rows.map((r) => r.original);
    if (exportRows.length === 0) { showToast('error', 'Dışa aktarılacak veri yok'); return; }
    const cols = [
      { field: 'title', headerName: 'Başlık' },
      { field: 'description', headerName: 'Açıklama' },
      { field: 'status', headerName: 'Durum' },
      { headerName: 'Öncelik', valueGetter: ({ row }) => priorityLabel(row.priority) },
      { field: 'customerName', headerName: 'Müşteri' },
      { headerName: 'Tarih', valueGetter: ({ row }) => formatDate(row.createdDate) },
    ];
    const dateStr = new Date().toISOString().slice(0, 10);
    exportToExcel(exportRows, cols, `aksiyonlar_${dateStr}`);
    showToast('success', `${exportRows.length} kayıt dışa aktarıldı`);
  };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilter(filterInput); }}
      />
      {globalFilter && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilter(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={load}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={pagination.pageIndex + 1}
      totalCount={filteredCount}
      pageSize={pagination.pageSize}
      rowCount={table.getRowModel().rows.length}
      onPageChange={(page) => setPagination((p) => ({ ...p, pageIndex: page - 1 }))}
      onPageSizeChange={(size) => setPagination({ pageIndex: 0, pageSize: size })}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Aksiyonlarım"
        subtitle="Atanan aksiyonları filtreleyin, sıralayın ve takip edin."
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          loading={loading}
          emptyText="Aksiyon bulunamadı"
        />
      </ListPageShell>
      <Toast toast={toast} />
    </div>
  );
}
